import React from 'react';
/**
 * DjayVariantPicker — The "Kies je stijl" long-press popover that lets
 * the learner pick a Djay variant (and shows the DJ Goud unlock reveal
 * the first time it's discovered).
 *
 * Extracted from FloatingDjay.tsx (audit P1, ronde 2 2026). Pure
 * presentation — receives unlocked variants + selected variant +
 * change handler. The egg-label is driven by a parent flag because the
 * timing of its dismissal is owned by the parent's interaction state.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, SparklesIcon } from 'lucide-react';
import { DjaySpecimen } from '../DjaySpecimen';
import type { VariantUnlockInfo } from '../useVariantUnlocks';
import type { DjayVariant } from '../DjayAvatar';
interface DjayVariantPickerProps {
  isOpen: boolean;
  variants: VariantUnlockInfo[];
  selectedVariant: DjayVariant;
  shouldAnimate: boolean;
  showEasterEggLabel: boolean;
  onSelect: (variant: DjayVariant) => void;
}
export function DjayVariantPicker({
  isOpen,
  variants,
  selectedVariant,
  shouldAnimate,
  showEasterEggLabel,
  onSelect
}: DjayVariantPickerProps) {
  return <AnimatePresence data-id="element-3405" data-mp="1nborb9gh5bhl">
      {isOpen && <motion.div initial={shouldAnimate ? {
      opacity: 0,
      y: 10,
      scale: 0.95
    } : false} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={shouldAnimate ? {
      opacity: 0,
      y: 10,
      scale: 0.95
    } : false} transition={{
      duration: 0.15,
      ease: [0.32, 0.72, 0, 1]
    }} className="absolute bottom-full right-0 mb-4 w-[280px] md:w-[340px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right pointer-events-auto" data-id="element-3406" data-mp="1nads7mxz4rwt">
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-3407" data-mp="2irdmjhajbb6" />

          <div className="relative z-10" data-id="element-3408" data-mp="3tx5fe9qu0xxy">
            <p className="text-xs text-slate-400 font-medium mb-3" data-id="element-3409" data-mp="3tw41zymcfzd2">
              Kies je stijl
            </p>

            <AnimatePresence data-id="element-3410" data-mp="3ovzmobx69aof">
              {showEasterEggLabel && <EasterEggLabel data-id="element-3411" data-mp="3k0an48l5jts4" />}
            </AnimatePresence>

            <div className="grid grid-cols-4 gap-2" data-id="element-3412" data-mp="lhc181jz3yk2">
              {variants.map(v => {
            const isSelected = selectedVariant === v.id;
            const isNewGold = showEasterEggLabel && v.id === 'goud';
            return <button key={v.id} onClick={() => {
              if (v.unlocked) onSelect(v.id);
            }} disabled={!v.unlocked} className="flex flex-col items-center gap-1.5 group focus:outline-none" title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-3413" data-mp="bhuxhpoa7uqq">
                    <motion.div animate={isNewGold ? {
                scale: [1, 1.15, 1]
              } : {}} transition={isNewGold ? {
                duration: 1,
                repeat: Infinity
              } : {}} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 relative
                        ${isNewGold ? 'ring-2 ring-yellow-400 bg-yellow-50 shadow-lg shadow-yellow-200/50' : ''}
                        ${isSelected && !isNewGold ? 'ring-2 ring-orange-400 scale-105 bg-orange-50 shadow-sm' : ''}
                        ${!isSelected && !isNewGold ? 'bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-slate-100' : ''}
                        ${!v.unlocked ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}
                      `} data-id="element-3414" data-mp="1br31ruxnyksd">
                      <div className="w-10 h-10" data-id="element-3415" data-mp="25ih3rjz217v2">
                        <DjaySpecimen look={{
                    variant: v.id
                  }} emotion="happy" size="sm" data-id="element-3416" data-mp="25js7xkwewhmp" />
                      </div>
                      {!v.unlocked && <div className="absolute inset-0 bg-slate-900/5 rounded-full flex items-center justify-center backdrop-blur-[0.5px]" data-id="element-3417" data-mp="1qmhp1qhfw9gy">
                          <LockIcon className="w-4 h-4 text-slate-500 drop-shadow-sm" data-id="element-3418" data-mp="y4neln4iw43a" />
                        </div>}
                    </motion.div>
                    <span className={`text-[10px] font-medium transition-colors leading-tight text-center
                        ${isNewGold ? 'text-yellow-600 font-bold' : ''}
                        ${isSelected && !isNewGold ? 'text-orange-600' : ''}
                        ${!isSelected && !isNewGold ? 'text-slate-500' : ''}
                      `} data-id="element-3419" data-mp="u1he6w43d74q">
                      {v.name}
                    </span>
                  </button>;
          })}
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
/** The "🏆 Geheim ontdekt!" banner that appears once when DJ Goud
 * unlocks for the first time. Kept inline because it's only used here. */
function EasterEggLabel() {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    y: -10
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.8,
    y: -10
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} className="mb-4 relative overflow-hidden rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 p-4" data-id="element-3420" data-mp="xoxed8f0a9gy">
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-3421" data-mp="1f6dyh2r7jtzk" />
      <div className="relative z-10 flex items-center gap-3" data-id="element-3422" data-mp="11asc73cczoau">
        <motion.div animate={{
        rotate: [0, 15, -15, 0],
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 0.6,
        repeat: 2
      }} data-id="element-3423" data-mp="wae4jmeo6gdc">
          <SparklesIcon className="w-8 h-8 text-yellow-500" data-id="element-3424" data-mp="2qmuizahmd0ta" />
        </motion.div>
        <div data-id="element-3425" data-mp="2lpucwzkrry26">
          <p className="font-black text-amber-900 text-base" data-id="element-3426" data-mp="2lpc7pdg4q9i4">
            🏆 Geheim ontdekt!
          </p>
          <p className="text-sm font-bold text-amber-700" data-id="element-3427" data-mp="2t5zc9et4q6sn">
            DJ Goud is ontgrendeld!
          </p>
        </div>
      </div>
      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" style={{
      left: `${15 + i * 15}%`,
      top: `${20 + i % 3 * 25}%`
    }} animate={{
      y: [0, -12, 0],
      opacity: [0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8]
    }} transition={{
      duration: 1.5,
      repeat: Infinity,
      delay: i * 0.2
    }} data-id="element-3428" data-mp="32z6ckl90iwlv" data-mpi={i} />)}
    </motion.div>;
}